import {
  AlertTriangle,
  Layers,
  Loader2,
  Plus,
  Send,
  TrendingUp,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "../ui/Toast";
import api from "../../services/api";
import ImageEditor from "./ImageEditor";
import { TAGS } from "./JournalQuickNote";

const PRICE_MODE_KEY = "tj_option_price_mode"; // "premium" | "total"

function createEmptyLeg(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    symbol: "",
    side: "BUY",
    quantity: "",
    entryPrice: "", // displayed value, interpreted per priceMode for options
    targetExitPrice: "", // planned exit — does NOT close the trade
    exitPrice: "", // actual exit — closes the trade when set
    optionType: "CALL",
    strike: "",
    expiration: "",
    ...overrides,
  };
}

// Forgiving date parser so typed dates "always take" (Safari's native date
// input is finicky). Accepts YYYY-MM-DD, MM/DD/YYYY, M/D/YY, etc. Returns a
// canonical YYYY-MM-DD, or "" if it can't be parsed.
// A real production incident: a mistyped "0026-11-22" (meant 2026) matched
// the YYYY-MM-DD pattern just as validly as a correct year — nothing here
// checked the year was plausible — and got stored as a real expiration
// date, which later caused a position auto-close feature to treat an
// active $8,000 option as already-expired-worthless and zero it out as an
// $800,000 loss. isPlausibleYear guards against that at the source.
const isPlausibleYear = (y) => y >= new Date().getFullYear() - 5 && y <= new Date().getFullYear() + 20;

const normalizeDate = (input) => {
  if (!input) return "";
  const s = String(input).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    if (!isPlausibleYear(parseInt(m[1], 10))) return "";
    return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  }
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) {
    let [, mo, d, y] = m;
    if (y.length === 2) y = "20" + y;
    if (!isPlausibleYear(parseInt(y, 10))) return "";
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return "";
};

// Convert a displayed price (premium or total) to per-share premium.
const toPerSharePremium = (value, mode, qty) => {
  const v = parseFloat(value);
  if (!v) return "";
  if (mode === "total") {
    const q = parseFloat(qty) || 0;
    if (!q) return "";
    return v / (100 * q);
  }
  return v;
};

const UnifiedComposer = ({ onSubmit, disabled, selectedDate, prefilledTransactions = [] }) => {
  const toast = useToast();

  // Note state
  const [note, setNote] = useState("");
  const [selectedTags, setSelectedTags] = useState({
    emotion: null,
    setup: null,
    mistakes: [],
  });
  const [confidence, setConfidence] = useState(5);
  const [pastedImage, setPastedImage] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Trade details state
  // Trade details are always shown now (no collapse) — kept as a constant so
  // the rest of the gating logic reads cleanly.
  const showTradeDetails = true;
  const [assetType, setAssetType] = useState("STOCK"); // STOCK, OPTION
  const [legs, setLegs] = useState([createEmptyLeg()]);
  const [strategy, setStrategy] = useState("");
  const [priceMode, setPriceMode] = useState(
    () => localStorage.getItem(PRICE_MODE_KEY) || "premium"
  );

  const [loading, setLoading] = useState(false);

  // Prefill legs from broker transactions selected via TransactionContext
  useEffect(() => {
    if (prefilledTransactions.length > 0) {
      const newLegs = prefilledTransactions.map((txn) =>
        createEmptyLeg({
          symbol: txn.underlying_symbol || txn.symbol,
          side: txn.side,
          quantity: Math.abs(txn.quantity),
          entryPrice: txn.price,
          exitPrice: txn.tradePnL ? txn.price : "",
          optionType: txn.put_call || "CALL",
          strike: txn.strike_price || "",
          expiration: txn.expiration_date
            ? new Date(txn.expiration_date).toISOString().split("T")[0]
            : "",
        })
      );

      setLegs(newLegs);
      setAssetType(prefilledTransactions.some((t) => t.asset_type === "OPTION") ? "OPTION" : "STOCK");
      // trade details are always visible now
    }
  }, [prefilledTransactions]);

  const setAndPersistPriceMode = (mode) => {
    setPriceMode(mode);
    localStorage.setItem(PRICE_MODE_KEY, mode);
  };

  const addLeg = () => {
    const lastLeg = legs[legs.length - 1];
    setLegs([
      ...legs,
      createEmptyLeg({
        symbol: lastLeg?.symbol || "",
        expiration: lastLeg?.expiration || "",
      }),
    ]);
  };

  const removeLeg = (id) => {
    if (legs.length > 1) {
      setLegs(legs.filter((l) => l.id !== id));
    }
  };

  const updateLeg = (id, field, value) => {
    setLegs(legs.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  const toggleMistake = (mistakeId) => {
    setSelectedTags((prev) => ({
      ...prev,
      mistakes: prev.mistakes.includes(mistakeId)
        ? prev.mistakes.filter((id) => id !== mistakeId)
        : [...prev.mistakes, mistakeId],
    }));
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        e.preventDefault();
        const blob = item.getAsFile();
        setPastedImage(blob);
        break;
      }
    }
  }, []);

  const handleImageSave = async (blob) => {
    setPastedImage(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("image", blob, "chart_annotation.jpg");

      const { data } = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        setAttachments((prev) => [
          ...prev,
          { url: data.data.url, id: Date.now().toString(), type: "image" },
        ]);
      }
    } catch (error) {
      console.error("Upload failed", error);
      toast.error("Failed to upload image.");
    } finally {
      setUploading(false);
    }
  };

  const detectStrategy = () => {
    if (legs.length === 1) return null;

    const options = legs.filter(() => assetType === "OPTION");
    if (options.length < 2) return null;

    const calls = options.filter((l) => l.optionType === "CALL");
    const puts = options.filter((l) => l.optionType === "PUT");
    const buys = options.filter((l) => l.side === "BUY");
    const sells = options.filter((l) => l.side === "SELL");

    if (options.length === 2 && buys.length === 1 && sells.length === 1) {
      if (calls.length === 2) return "Call Spread";
      if (puts.length === 2) return "Put Spread";
    }

    if (calls.length === 1 && puts.length === 1) {
      if (calls[0].strike === puts[0].strike) {
        return buys.length === 2 ? "Long Straddle" : "Short Straddle";
      } else {
        return buys.length === 2 ? "Long Strangle" : "Short Strangle";
      }
    }

    if (options.length === 4) return "Iron Condor";

    return `${legs.length}-Leg Strategy`;
  };

  // "Did you mean?" hint: in premium mode, a value >= 1000 is suspiciously
  // large for a per-share premium and likely meant as a total spend.
  const looksLikeTotalHint = (leg) => {
    if (assetType !== "OPTION" || priceMode !== "premium") return null;
    const v = parseFloat(leg.entryPrice);
    if (!v || v < 1000) return null;
    const qty = parseFloat(leg.quantity) || 1;
    const impliedTotal = v;
    const impliedPremium = v / (100 * qty);
    return `Did you mean $${impliedPremium.toFixed(2)}/share (total $${impliedTotal.toFixed(2)})?`;
  };

  const hasNote =
    note.trim() || selectedTags.emotion || selectedTags.setup || attachments.length > 0;

  const validLegs = legs.filter((l) => l.symbol && l.entryPrice && l.quantity);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasNote && validLegs.length === 0) {
      toast.warning("Add a note or at least one trade leg before posting.");
      return;
    }

    if (showTradeDetails && assetType === "OPTION" && validLegs.length > 0) {
      const incomplete = validLegs.some((l) => !l.strike || !normalizeDate(l.expiration));
      if (incomplete) {
        toast.warning("Each option leg needs a strike price and a valid expiration date (MM/DD/YYYY).");
        return;
      }
    }

    const detectedStrategy = strategy || detectStrategy();
    const unit = assetType === "OPTION" ? priceMode : "premium";

    const payloadLegs =
      showTradeDetails && validLegs.length > 0
        ? validLegs.map((leg) => ({
            symbol: leg.symbol.toUpperCase(),
            side: leg.side,
            quantity: parseFloat(leg.quantity),
            entryPrice: toPerSharePremium(leg.entryPrice, unit, leg.quantity),
            // Actual exit closes the trade; blank keeps it open.
            exitPrice: leg.exitPrice
              ? toPerSharePremium(leg.exitPrice, unit, leg.quantity)
              : null,
            // Target exit is a plan — stored, never closes the trade.
            targetExitPrice: leg.targetExitPrice
              ? toPerSharePremium(leg.targetExitPrice, unit, leg.quantity)
              : null,
            optionType: assetType === "OPTION" ? leg.optionType : null,
            strike: assetType === "OPTION" && leg.strike ? parseFloat(leg.strike) : null,
            expiration: assetType === "OPTION" ? normalizeDate(leg.expiration) || null : null,
            underlyingPriceAtEntry: null,
            entryIv: null,
          }))
        : [];

    setLoading(true);

    try {
      const payload = {
        note: note.trim() || null,
        emotionalState: selectedTags.emotion,
        tradeReason: selectedTags.setup,
        mistakesMade: selectedTags.mistakes.join(", "),
        confidenceLevel: confidence,
        attachments,
        entryDate: selectedDate || new Date().toISOString().split("T")[0],
        assetType,
        strategy: detectedStrategy,
        legs: payloadLegs,
      };

      const { data } = await api.post("/journal/log", payload);

      if (data.success) {
        onSubmit(data.data.entry);
      }

      // Reset
      setNote("");
      setSelectedTags({ emotion: null, setup: null, mistakes: [] });
      setConfidence(5);
      setAttachments([]);
      setLegs([createEmptyLeg()]);
      setStrategy("");
      setAssetType("STOCK");
    } catch (error) {
      console.error("Log Entry Error:", error);
      const message =
        error.response?.data?.error ||
        error.response?.data?.errors?.[0]?.msg ||
        "Failed to save entry.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {attachments.length === 0 && !note && (
        <div className="absolute top-14 right-4 pointer-events-none opacity-50 text-xs text-slate-500 hidden sm:block z-10">
          Tip: Paste (⌘+V) an image
        </div>
      )}

      <form onSubmit={handleSubmit} onPaste={handlePaste} className="space-y-4">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full bg-slate-900/50 text-slate-200 rounded-lg p-3 border border-slate-700 focus:outline-none focus:border-indigo-500 placeholder-slate-500 resize-none transition-all"
          rows="3"
          placeholder="What's on your mind?"
          disabled={disabled || uploading}
        />

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {attachments.map((att) => (
              <div
                key={att.id}
                className="relative group flex-shrink-0 h-20 w-32 bg-slate-900 rounded border border-slate-700 overflow-hidden"
              >
                <img src={att.url} alt="Attachment" className="h-full w-full object-cover" />
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((a) => a.id !== att.id))}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
            {uploading && (
              <div className="h-20 w-32 bg-slate-900 rounded border border-slate-700 border-dashed flex items-center justify-center">
                <Loader2 className="animate-spin text-indigo-500" />
              </div>
            )}
          </div>
        )}

        {/* Mood / Setup chips */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-500 uppercase w-14">Mood</span>
            {TAGS.emotions.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() =>
                  setSelectedTags((prev) => ({
                    ...prev,
                    emotion: prev.emotion === tag.id ? null : tag.id,
                  }))
                }
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  selectedTags.emotion === tag.id
                    ? `${tag.color} ring-1 ring-offset-1 ring-offset-slate-800 ring-slate-500`
                    : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"
                }`}
              >
                <tag.icon size={12} />
                {tag.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-500 uppercase w-14">Setup</span>
            {TAGS.setups.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() =>
                  setSelectedTags((prev) => ({
                    ...prev,
                    setup: prev.setup === tag.label ? null : tag.label,
                  }))
                }
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  selectedTags.setup === tag.label
                    ? `${tag.color} ring-1 ring-offset-1 ring-offset-slate-800 ring-slate-500`
                    : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"
                }`}
              >
                <tag.icon size={12} />
                {tag.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-500 uppercase w-14">Mistake</span>
            {TAGS.mistakes.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleMistake(tag.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  selectedTags.mistakes.includes(tag.id)
                    ? `${tag.color} ring-1 ring-offset-1 ring-offset-slate-800 ring-slate-500`
                    : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-600 hover:text-slate-300"
                }`}
              >
                <tag.icon size={12} />
                {tag.label}
              </button>
            ))}
          </div>
        </div>

        <div className="h-px bg-slate-700/50 w-full" />

        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Confidence
          </span>
          <div className="flex-1 sm:w-48">
            <input
              type="range"
              min="1"
              max="10"
              value={confidence}
              onChange={(e) => setConfidence(parseInt(e.target.value))}
              className="w-full h-1.5 bg-slate-700 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-indigo-500"
            />
          </div>
          <span
            className={`text-xs font-bold w-6 text-center ${
              confidence >= 8 ? "text-emerald-400" : confidence >= 5 ? "text-yellow-400" : "text-red-400"
            }`}
          >
            {confidence}
          </span>
        </div>

        <div className="h-px bg-slate-700/50 w-full" />

        {/* Trade Details — always shown */}
        <div className="flex items-center gap-1.5 text-sm font-medium text-indigo-400">
          <TrendingUp size={16} />
          Trade details
          <span className="text-xs font-normal text-slate-500">(optional — leave blank for a note-only entry)</span>
        </div>

        {showTradeDetails && (
          <div className="space-y-4 animate-fade-in">
            {/* Asset Type Toggle */}
            <div className="flex items-center gap-2 p-1 bg-slate-900 rounded-lg w-fit">
              <button
                type="button"
                onClick={() => setAssetType("STOCK")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  assetType === "STOCK" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                📈 Stock
              </button>
              <button
                type="button"
                onClick={() => setAssetType("OPTION")}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  assetType === "OPTION" ? "bg-purple-600 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                📊 Option
              </button>

              {assetType === "OPTION" && (
                <div className="flex items-center gap-1 ml-2 p-1 bg-slate-800 rounded-md">
                  <button
                    type="button"
                    onClick={() => setAndPersistPriceMode("premium")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      priceMode === "premium" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Premium / share
                  </button>
                  <button
                    type="button"
                    onClick={() => setAndPersistPriceMode("total")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      priceMode === "total" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"
                    }`}
                  >
                    Total spent
                  </button>
                </div>
              )}
            </div>

            {legs.length > 1 && detectStrategy() && (
              <div className="flex items-center gap-2 text-xs">
                <Layers size={14} className="text-indigo-400" />
                <span className="text-indigo-400 font-medium">Detected: {detectStrategy()}</span>
              </div>
            )}

            {/* Legs */}
            <div className="space-y-3">
              {legs.map((leg, index) => {
                const hint = looksLikeTotalHint(leg);
                const convertedPreview =
                  assetType === "OPTION" && leg.entryPrice && leg.quantity
                    ? priceMode === "premium"
                      ? `≈ $${(parseFloat(leg.entryPrice) * 100 * (parseFloat(leg.quantity) || 0)).toFixed(2)} total`
                      : `≈ $${toPerSharePremium(leg.entryPrice, "total", leg.quantity).toFixed(2)}/share`
                    : null;

                return (
                  <div
                    key={leg.id}
                    className="bg-slate-900/50 rounded-lg border border-slate-700 p-4 space-y-3"
                  >
                    {legs.length > 1 && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-500 font-medium">LEG {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeLeg(leg.id)}
                          className="text-slate-500 hover:text-red-400 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          {assetType === "OPTION" ? "Underlying" : "Symbol"}
                        </label>
                        <input
                          value={leg.symbol}
                          onChange={(e) => updateLeg(leg.id, "symbol", e.target.value.toUpperCase())}
                          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono placeholder-slate-600 focus:border-indigo-500 outline-none"
                          placeholder="AAPL"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Side</label>
                        <div className="flex bg-slate-800 rounded border border-slate-700 p-1">
                          <button
                            type="button"
                            onClick={() => updateLeg(leg.id, "side", "BUY")}
                            className={`flex-1 text-xs font-bold py-1.5 rounded ${
                              leg.side === "BUY" ? "bg-emerald-600 text-white" : "text-slate-400 hover:text-white"
                            }`}
                          >
                            BUY
                          </button>
                          <button
                            type="button"
                            onClick={() => updateLeg(leg.id, "side", "SELL")}
                            className={`flex-1 text-xs font-bold py-1.5 rounded ${
                              leg.side === "SELL" ? "bg-red-600 text-white" : "text-slate-400 hover:text-white"
                            }`}
                          >
                            SELL
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          {assetType === "OPTION" ? "Contracts" : "Shares"}
                        </label>
                        <input
                          type="number"
                          value={leg.quantity}
                          onChange={(e) => updateLeg(leg.id, "quantity", e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono focus:border-indigo-500 outline-none"
                          placeholder={assetType === "OPTION" ? "5" : "100"}
                        />
                      </div>
                    </div>

                    {assetType === "OPTION" && (
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Type</label>
                          <div className="flex bg-slate-800 rounded border border-slate-700 p-1">
                            <button
                              type="button"
                              onClick={() => updateLeg(leg.id, "optionType", "CALL")}
                              className={`flex-1 text-xs font-bold py-1.5 rounded ${
                                leg.optionType === "CALL" ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"
                              }`}
                            >
                              CALL
                            </button>
                            <button
                              type="button"
                              onClick={() => updateLeg(leg.id, "optionType", "PUT")}
                              className={`flex-1 text-xs font-bold py-1.5 rounded ${
                                leg.optionType === "PUT" ? "bg-orange-600 text-white" : "text-slate-400 hover:text-white"
                              }`}
                            >
                              PUT
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Strike</label>
                          <input
                            type="number"
                            step="0.5"
                            value={leg.strike}
                            onChange={(e) => updateLeg(leg.id, "strike", e.target.value)}
                            className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono focus:border-indigo-500 outline-none"
                            placeholder="250"
                          />
                        </div>

                        <div>
                          <label className="text-xs text-slate-500 block mb-1">Expiration</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="MM/DD/YYYY"
                            value={leg.expiration}
                            onChange={(e) => updateLeg(leg.id, "expiration", e.target.value)}
                            onBlur={(e) => {
                              const norm = normalizeDate(e.target.value);
                              if (norm) updateLeg(leg.id, "expiration", norm);
                            }}
                            className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono focus:border-indigo-500 outline-none"
                          />
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          {assetType === "OPTION"
                            ? priceMode === "premium"
                              ? "Entry Premium / share"
                              : "Entry Total Spent"
                            : "Entry Price"}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={leg.entryPrice}
                          onChange={(e) => updateLeg(leg.id, "entryPrice", e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono focus:border-indigo-500 outline-none"
                        />
                        {convertedPreview && (
                          <p className="text-[11px] text-slate-500 mt-1">{convertedPreview}</p>
                        )}
                        {hint && (
                          <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1">
                            <AlertTriangle size={10} /> {hint}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          Target Exit <span className="text-slate-600">(optional)</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={leg.targetExitPrice}
                          onChange={(e) => updateLeg(leg.id, "targetExitPrice", e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-amber-300 font-mono focus:border-indigo-500 outline-none"
                          placeholder="planned — won't close"
                        />
                      </div>

                      <div>
                        <label className="text-xs text-slate-500 block mb-1">
                          {assetType === "OPTION"
                            ? priceMode === "premium"
                              ? "Actual Exit / share"
                              : "Actual Exit Total"
                            : "Actual Exit"}
                          <span className="text-slate-600"> (closes)</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={leg.exitPrice}
                          onChange={(e) => updateLeg(leg.id, "exitPrice", e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white font-mono focus:border-indigo-500 outline-none"
                          placeholder="blank = still open"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={addLeg}
              className="flex items-center gap-1 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
            >
              <Plus size={16} />
              Add Leg
            </button>

            <div>
              <label className="text-xs text-slate-500 block mb-1">Strategy / Setup</label>
              <input
                value={strategy}
                onChange={(e) => setStrategy(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-white placeholder-slate-600 focus:border-indigo-500 outline-none"
                placeholder={detectStrategy() || "e.g., Earnings Play, Breakout, Credit Spread..."}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={disabled || loading || uploading || (!hasNote && validLegs.length === 0)}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white px-6 py-2 rounded-lg font-medium transition-all shadow-lg flex items-center justify-center gap-2 text-sm"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                <Send size={16} />
                Post
              </>
            )}
          </button>
        </div>
      </form>

      {pastedImage && (
        <ImageEditor
          imageFile={pastedImage}
          onSave={handleImageSave}
          onCancel={() => setPastedImage(null)}
        />
      )}
    </div>
  );
};

export default UnifiedComposer;
